import { AtIcon } from '@phosphor-icons/react';
import React from 'react';
import { toast } from 'sonner';

import {
  SlackMentionList,
  type SlackMentionListHandle,
} from '@/components/create/v2/composer/SlackMentionList';
import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { CritiqueFinding } from '@/lib/slack/editorial-note-tag';
import { type SlackMentionResult, fetchSlackMentions } from '@/lib/slack/fetch-slack-mentions';
import { type EditorialTagFailure, postEditorialTag } from '@/lib/slack/post-editorial-tag';
import { ITEM_META } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

type Picked = { kind: 'member' | 'channel'; id: string; name: string };

const NOTE_MAX = 280;

/**
 * "Tag in Slack" on an open editorial review note. Picks a person (or channel) from the
 * workspace's Slack with the same search and the same rows as the Home composer's `@`-mention
 * (`fetchSlackMentions` + `SlackMentionList`, inline variant), takes an optional one-line
 * message, and sends one Slack message about the note through `POST /api/slack/tag-note`.
 *
 * When Slack will not open a DM with the person (the app does not request `im:write`), the
 * server says so and the picker asks for a channel instead, keeping the person to mention
 * there. It never reports a send that did not happen.
 */
export function TagNoteButton({
  topicId,
  finding,
}: {
  topicId: string;
  finding: CritiqueFinding;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [items, setItems] = React.useState<SlackMentionResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [picked, setPicked] = React.useState<Picked | null>(null);
  /** The person a DM could not reach; set, the picker lists channels to mention them in. */
  const [mentionPerson, setMentionPerson] = React.useState<Picked | null>(null);
  const [note, setNote] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const listRef = React.useRef<SlackMentionListHandle>(null);
  const { openSettings } = useSettingsDialog();

  const reset = () => {
    setQuery('');
    setItems([]);
    setPicked(null);
    setMentionPerson(null);
    setNote('');
    setError(null);
  };

  React.useEffect(() => {
    if (!open || picked) return;
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      void fetchSlackMentions(query, controller.signal).then((results) => {
        if (controller.signal.aborted) return;
        setItems(results);
        setLoading(false);
      });
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, picked, query]);

  const notConnected = items.length === 1 && items[0].kind === 'connect-prompt';
  const shown = mentionPerson ? items.filter((item) => item.kind === 'channel') : items;

  const pick = ({ id }: { id: string; label: string }) => {
    const item = shown.find(
      (candidate) => candidate.kind !== 'connect-prompt' && candidate.id === id,
    );
    if (!item || item.kind === 'connect-prompt') return;
    setPicked({ kind: item.kind, id: item.id, name: item.name });
    setError(null);
  };

  const send = async () => {
    if (!picked || sending) return;
    setSending(true);
    setError(null);
    const result = await postEditorialTag({
      topicId,
      finding,
      recipient: { kind: picked.kind, id: picked.id },
      mentionUserId: picked.kind === 'channel' ? (mentionPerson?.id ?? null) : null,
      note,
    });
    setSending(false);
    if (result.ok) {
      toast.success(
        picked.kind === 'channel'
          ? `Posted in #${picked.name}${mentionPerson ? `, mentioning ${mentionPerson.name}` : ''}`
          : `Sent to ${picked.name} in Slack`,
      );
      setOpen(false);
      reset();
      return;
    }
    const failed = result as EditorialTagFailure;
    if (failed.code === 'slack_tag_dm_unavailable' && picked.kind === 'member') {
      setMentionPerson(picked);
      setPicked(null);
      setQuery('');
      return;
    }
    setError(failed.message);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <AtIcon />
          Tag in Slack
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="space-y-3">
        {picked ? (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <PlatformBrandIcon platform="slack" className="size-3.5 shrink-0" />
              <p className="min-w-0 flex-1 truncate text-caption font-medium text-foreground">
                {picked.kind === 'channel' ? `#${picked.name}` : picked.name}
              </p>
              <Button
                variant="flat"
                size="sm"
                disabled={sending}
                onClick={() => {
                  setPicked(null);
                  setError(null);
                }}
              >
                Change
              </Button>
            </div>
            {mentionPerson ? (
              <p className={ITEM_META}>{`${mentionPerson.name} will be mentioned in the post.`}</p>
            ) : null}
            <Input
              autoFocus
              value={note}
              maxLength={NOTE_MAX}
              placeholder="Add a message (optional)"
              aria-label="Message to send with the note"
              onChange={(event) => setNote(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void send();
                }
              }}
            />
            <p className={ITEM_META}>Sends the note, its quoted passage and a link back to it.</p>
            {error ? (
              <p role="alert" className={cn(ITEM_META, 'text-destructive')}>
                {error}
              </p>
            ) : null}
            <div className="flex justify-end">
              <Button variant="neutral" size="sm" disabled={sending} onClick={() => void send()}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </>
        ) : notConnected ? (
          <div className="space-y-2">
            <p className="text-caption text-foreground">
              Slack is not connected for this workspace.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false);
                openSettings('integrations');
              }}
            >
              Connect Slack in Settings &gt; Connections
            </Button>
          </div>
        ) : (
          <>
            {mentionPerson ? (
              <p className={ITEM_META}>
                {`Mora could not message ${mentionPerson.name} directly. Pick a channel to post the note in; they will be mentioned there.`}
              </p>
            ) : null}
            <Input
              autoFocus
              value={query}
              placeholder={mentionPerson ? 'Search channels' : 'Search people or channels'}
              aria-label="Search Slack"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (listRef.current?.onKeyDown({ event: event.nativeEvent })) {
                  event.preventDefault();
                }
              }}
            />
            <div className="-mx-2">
              <SlackMentionList
                ref={listRef}
                variant="inline"
                items={shown}
                loading={loading && shown.length === 0}
                command={pick}
              />
            </div>
            {!loading && shown.length === 0 ? (
              <p className={ITEM_META}>
                {query.trim()
                  ? `Nothing in Slack matches “${query.trim()}”.`
                  : mentionPerson
                    ? 'No channels found in Slack.'
                    : 'No one found in Slack.'}
              </p>
            ) : null}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
